import type { ChatMessages } from "@storytell/cp-client";
import { TbApps, TbChevronRight } from "solid-icons/tb";
import { createSignal, For, Show } from "solid-js";

interface ConceptsRendererProps {
  conceptResults: ChatMessages.ConceptResult;
}

interface ConceptSummaryItemProps {
  concept: ChatMessages.ConceptSummary;
  index: number;
}

const ConceptSummaryItem = (props: ConceptSummaryItemProps) => {
  return (
    <div class="rounded-lg border border-gray-200 p-4 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50">
      <div class="flex items-start gap-3">
        {/* Icon container */}
        <div class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
          <TbApps class="size-4 text-blue-600 dark:text-blue-400" />
        </div>

        <div class="min-w-0 flex-1">
          {/* Title with index */}
          <div class="mb-1 flex items-center gap-2">
            <h4 class="flex-1 truncate text-sm font-medium text-gray-900 dark:text-gray-100">{props.concept.name}</h4>
            <span class="shrink-0 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              #{props.index + 1}
            </span>
          </div>

          {/* Domain if present */}
          <Show when={props.concept.domain}>
            <div class="mb-2 text-xs text-blue-600 dark:text-blue-400">{props.concept.domain}</div>
          </Show>

          {/* Summary in italic if present */}
          <Show when={props.concept.summary}>
            <p class="line-clamp-2 text-sm text-gray-600 italic dark:text-gray-400">{props.concept.summary}</p>
          </Show>

          {/* Description - only show if no summary or if it's different */}
          <Show
            when={
              props.concept.description &&
              (!props.concept.summary || props.concept.summary !== props.concept.description)
            }
          >
            <p class="mt-1 line-clamp-2 text-sm text-gray-700 dark:text-gray-300">
              {props.concept.description.length > 150
                ? `${props.concept.description.substring(0, 150)}...`
                : props.concept.description}
            </p>
          </Show>
        </div>
      </div>
    </div>
  );
};

/**
 * Renders concept results in a structured format with expandable list
 */
export const ConceptsRenderer = (props: ConceptsRendererProps) => {
  const duration = () => {
    const raw = props.conceptResults.duration;
    if (!raw) return "0 seconds";

    let totalSeconds: number;
    if (raw.seconds !== undefined) {
      totalSeconds = Number(raw.seconds) + (raw.nanos || 0) / 1000000000;
    } else {
      const durationNum = Number(raw);
      totalSeconds = !Number.isNaN(durationNum) ? durationNum : 0;
    }
    // Round to nearest 0.1
    const rounded = Math.round(totalSeconds * 10) / 10;
    const label = rounded === 1 ? "second" : "seconds";
    return `${rounded} ${label}`;
  };

  const [showConcepts, setShowConcepts] = createSignal(true); // Start expanded for better UX

  return (
    // An agent fans these out several per turn, so a miss draws nothing.
    <Show when={props.conceptResults.concepts && props.conceptResults.concepts.length > 0}>
      <div>
        <div class="mb-2 flex items-center gap-1">
          <TbApps class="size-4 text-muted-text" />
          <h3 class="text-sm text-secondary-text">Loaded Concepts</h3>
        </div>

        <button
          type="button"
          onClick={() => setShowConcepts((prev) => !prev)}
          class="group/btn mb-2 flex cursor-pointer items-center gap-2"
        >
          <TbChevronRight
            class="size-3.5 shrink-0 stroke-3 text-secondary-text transition-transform"
            classList={{ "rotate-90": showConcepts() }}
          />
          <p class="text-sm font-medium text-blue-800 group-hover/btn:underline dark:text-blue-400">
            Loaded {props.conceptResults.totalConcepts || props.conceptResults.concepts.length} concept
            {(props.conceptResults.totalConcepts || props.conceptResults.concepts.length) !== 1 ? "s" : ""} in{" "}
            {duration()}
          </p>
        </button>

        <Show when={showConcepts()}>
          <div class="space-y-3">
            <For each={props.conceptResults.concepts}>
              {(concept, index) => <ConceptSummaryItem concept={concept} index={index()} />}
            </For>
          </div>
        </Show>
      </div>
    </Show>
  );
};
